import React from 'react';
import { useTranslations } from 'next-intl';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import ProtectPdfDialog from './ProtectPdfDialog';
import ProtectPdfField from './ProtectPdfField';
import ProtectPdfButton from './ProtectPdfButton';
import { UNLOCK_PDF_TOOL } from '../../../utils/unlockPdf';
import { formatAnalyticsSource } from '@/helpers/common';

export type ProtectPdfUnlockResult = 'unlocked' | 'wrong_password' | 'failed';

export type ProtectPdfCurrentPasswordModalData = {
  requestId?: number;
  fileName?: string;
  analyticsSource?: string;
  onUnlock: (password: string) => Promise<ProtectPdfUnlockResult>;
  onDismiss: () => void;
};

type CurrentPasswordError = 'empty' | 'incorrect' | 'failed';

type ProtectPdfCurrentPasswordModalProps = {
  data?: ProtectPdfCurrentPasswordModalData;
  hideModal: () => void;
};

const ProtectPdfCurrentPasswordForm = ({
  data,
  hideModal,
}: ProtectPdfCurrentPasswordModalProps) => {
  const t = useTranslations('protect_pdf_flow');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<CurrentPasswordError | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const isSubmittingRef = React.useRef(false);
  const analyticsSource = data?.analyticsSource;

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_PASSWORD_PROTECTED_PDF,
      source: analyticsSource,
    });

    if (analyticsSource === formatAnalyticsSource(UNLOCK_PDF_TOOL)) {
      logHubxEvent({
        eventName: APP_EVENTS.LND_PASSWORD_INPUT,
        source: analyticsSource,
      });
    }
  }, [analyticsSource]);

  const errorMessage =
    error === 'empty'
      ? t('empty_password_error')
      : error === 'incorrect'
        ? t('incorrect_password_error')
        : error === 'failed'
          ? t('unlock_failed')
          : null;
  const canSubmit = password.length > 0 && error !== 'incorrect';

  const handleChange = (value: string) => {
    setPassword(value);
    setError(null);
  };

  const handleDismiss = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    data?.onDismiss();
    hideModal();
  }, [data, hideModal]);

  const submit = React.useCallback(async () => {
    if (isSubmittingRef.current || !data) {
      return;
    }

    if (password.length === 0) {
      setError('empty');
      return;
    }

    if (error === 'incorrect') {
      return;
    }

    logHubxEvent({
      eventName: APP_EVENTS.BTN_PASSWORD_CONTINUE,
      source: analyticsSource,
    });

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const result = await data.onUnlock(password);

    isSubmittingRef.current = false;

    if (result === 'unlocked' || !isMountedRef.current) {
      return;
    }

    setIsSubmitting(false);
    setError(result === 'wrong_password' ? 'incorrect' : 'failed');
  }, [analyticsSource, data, error, password]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      !(event.target instanceof HTMLInputElement)
    ) {
      return;
    }

    event.preventDefault();
    void submit();
  };

  return (
    <ProtectPdfDialog
      name="ProtectPdfCurrentPassword"
      tone="lock"
      title={t('current_password_title')}
      description={
        data?.fileName
          ? t('current_password_file_description', { fileName: data.fileName })
          : t('current_password_description')
      }
      onDismiss={handleDismiss}
      showCloseIcon
      isDismissable={false}
      isKeyboardDismissDisabled={isSubmitting}
    >
      <form
        noValidate
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="flex flex-col gap-8"
      >
        <ProtectPdfField
          id="protect-pdf-current-password"
          label={t('current_password_label')}
          placeholder={t('password_placeholder')}
          value={password}
          onChange={handleChange}
          autoFocus
          errorMessage={errorMessage}
        />

        <ProtectPdfButton
          type="submit"
          isDisabled={!canSubmit}
          isLoading={isSubmitting}
        >
          {t('continue_button')}
        </ProtectPdfButton>
      </form>
    </ProtectPdfDialog>
  );
};

const ProtectPdfCurrentPasswordModal = (
  props: ProtectPdfCurrentPasswordModalProps
) => (
  <ProtectPdfCurrentPasswordForm
    key={props.data?.requestId ?? 'current-password'}
    {...props}
  />
);

export default ProtectPdfCurrentPasswordModal;
